import { Stack, Typography } from '@mui/material';
import { isEmpty, isNumber } from 'lodash';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { DescriptionRow, StylizedNumber } from 'components';
import { TranslationNamespace } from 'i18n';
import { P2PProvider } from 'types';
import { formatUtils } from 'utils';

import { ExpandableTradeMethods } from '../ExpandableTradeMethods';
import { P2PProviderCredentialsDetails } from '../P2PProviderCredentialsDetails';
import { P2PProviderOrderAmountLimitsDetails } from '../P2PProviderOrderAmountLimitsDetails';

type Props = {
  p2pProvider: P2PProvider;
};
export const P2PProviderPayinConfigDetails: React.FC<Props> = ({
  p2pProvider,
}) => {
  const { fiatCurrencyId, payinConfig: config, payinLimits } = p2pProvider;

  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  return (
    <Fragment>
      <Typography variant="h6">{t('details.payin_details')}</Typography>
      <DescriptionRow
        title={t('details.status.title')}
        value={
          config?.enabled
            ? t('details.status.enabled')
            : t('details.status.disabled')
        }
      />
      {config?.url && (
        <DescriptionRow title={t('details.url')} value={config.url} canCopy />
      )}

      <P2PProviderCredentialsDetails credentials={config?.credentials} />

      {config?.responseTimeout && (
        <DescriptionRow
          title={t('details.response_timeout')}
          description={t('details.response_timeout_description')}
          value={`${formatUtils.formatNumber(config.responseTimeout)}${tCommon(
            'suffixes.ms',
          )}`}
        />
      )}

      {isNumber(config?.expectedFee) && (
        <DescriptionRow
          title={t('details.expected_fee')}
          value={<StylizedNumber value={config.expectedFee} unit="%" />}
        />
      )}

      {config?.lastTakenOrderAt && (
        <DescriptionRow
          title={t('details.last_taken_order_at')}
          value={formatUtils.formatDate(config.lastTakenOrderAt)}
        />
      )}

      {config?.tradeMethods && !isEmpty(config.tradeMethods) && (
        <DescriptionRow
          title={t('details.trade_methods')}
          value={<ExpandableTradeMethods tradeMethods={config.tradeMethods} />}
        />
      )}

      <P2PProviderOrderAmountLimitsDetails
        fiatCurrencyId={fiatCurrencyId}
        config={config}
      />

      {!!config?.activeDisputeCount && (
        <DescriptionRow
          title={t('details.active_dispute_count')}
          value={
            <Stack direction="row" spacing={1}>
              <StylizedNumber
                value={
                  isNumber(payinLimits?.activeDisputeCount)
                    ? payinLimits?.activeDisputeCount
                    : '-'
                }
              />
              <span>{'/'}</span>
              <StylizedNumber value={config.activeDisputeCount} />
            </Stack>
          }
        />
      )}
    </Fragment>
  );
};
